/**
 * HistoryThumbnails Component
 * Displays the last 3 classification results as clickable card overlays
 * on the video stream, showing minimized classification data
 * 
 * Requirements: 8.1, 8.2, 8.3
 */

import { useState, useEffect } from 'react';
import { ClassificationResult } from '../types/index';
import { HistoryService } from '../services/HistoryService';
import './HistoryThumbnails.css';

interface HistoryThumbnailsProps {
  onItemClick: (result: ClassificationResult) => void;
  className?: string;
}

function HistoryThumbnails({ onItemClick, className }: HistoryThumbnailsProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [history, setHistory] = useState<ClassificationResult[]>([]);
  const [historyService] = useState(() => new HistoryService());
  
  // Load recent history on mount
  useEffect(() => {
    const loadRecentHistory = async () => {
      try {
        const historyData = await historyService.getHistory(3, 0); // Get only 3 most recent
        setHistory(historyData);
      } catch (err) {
        console.error('Failed to load recent history for thumbnails:', err);
        // Don't show error for history loading failure
      }
    };

    loadRecentHistory();
  }, [historyService]);
  
  // Show only the last 3 items
  const recentHistory = history.slice(0, 3);

  if (recentHistory.length === 0) {
    return null;
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getAverageConfidence = (result: ClassificationResult) => {
    const classified = result.criteria_results.filter(
      (cr) => cr.status === 'classified'
    );

    if (classified.length === 0) return null;

    const avgConfidence =
      classified.reduce((sum, cr) => sum + cr.confidence, 0) / classified.length;

    return Math.round(avgConfidence * 100);
  };

  const getTopCategories = (result: ClassificationResult) => {
    return result.criteria_results
      .filter((cr) => cr.status === 'classified')
      .slice(0, 2) // Show top 2 criteria
      .map((cr) => ({
        name: cr.criterion_name,
        category: cr.selected_category,
        confidence: Math.round(cr.confidence * 100)
      }));
  };

  return (
    <div className={`history-thumbnails-overlay ${isCollapsed ? 'collapsed' : ''} ${className || ''}`}>
      <div className="history-thumbnails-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span>Recent</span>
        <button
          className="collapse-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand history' : 'Collapse history'}
        >
          <svg 
            width="14" 
            height="14" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>
      {!isCollapsed && (
        <div className="history-thumbnails-list">
        {recentHistory.map((result, index) => {
          const confidence = getAverageConfidence(result);
          const topCategories = getTopCategories(result);
          
          return (
            <button
              key={result.frame_id}
              className="history-card"
              onClick={() => onItemClick(result)}
              aria-label={`View classification from ${formatTime(result.timestamp)}`}
            >
              {/* Card header */}
              <div className="card-header">
                <span className="card-badge">{index + 1}</span>
                <span className="card-time">{formatTime(result.timestamp)}</span>
              </div>

              {/* Card content - classification summary */}
              <div className="card-content">
                {topCategories.length > 0 ? (
                  topCategories.map((cat, idx) => (
                    <div key={idx} className="card-criterion">
                      <span className="criterion-name">{cat.name}</span>
                      <span className="criterion-category">{cat.category}</span>
                    </div>
                  ))
                ) : (
                  <div className="card-empty">No classifications</div>
                )}
              </div>

              {/* Card footer - confidence */}
              {confidence !== null && (
                <div className="card-footer">
                  <div className="confidence-bar">
                    <div 
                      className="confidence-fill" 
                      style={{ width: `${confidence}%` }}
                    />
                  </div>
                  <span className="confidence-text">{confidence}%</span>
                </div>
              )}
            </button>
          );
        })}
        </div>
      )}
    </div>
  );
}

export default HistoryThumbnails;
